import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { EventImpl } from '@fullcalendar/core/internal';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subject, takeUntil } from 'rxjs';
import { OverrideTypeLookup } from 'src/app/_stealth/shared/Enum/lookups';
import { ToastService } from 'src/app/modules/core/services/toast.service';
import { DeviceTemplate } from 'src/app/pages/main/_models/Devices/Device';
import { DeleteOverrideDto } from 'src/app/pages/main/_models/Monitoring/Overrides';
import { CalendarSchedule, ScheduleExceptionDto } from 'src/app/pages/main/_models/Monitoring/Schedules';
import { SelectList } from 'src/app/pages/main/_models/Monitoring/assign-override';
import { MonitoringService } from 'src/app/pages/main/_services/monitoring.service';
import { SchedulerService } from 'src/app/pages/main/_services/scheduler.service';

@Component({
    selector: 'app-override-modal',
    templateUrl: './override-modal.component.html',
    standalone: false
})
export class OverrideModalComponent implements OnInit, OnDestroy {

  @Input() calendarSchedules: CalendarSchedule[];
  @Input() calSchedOverride: CalendarSchedule;
  @Input() adjustedCalendarSchedules: CalendarSchedule[];
  @Input() originalStartDate: Date;
  @Input() originalEndDate: Date
  @Input() calEventsToOverride: EventImpl[];
  @Input() selectedCustomerName: string;
  @Input() selectedSiteName: string;
  @Input() selectedNvrName: string;

  //Form Controls
  public createScheduleOverrideForm: FormGroup;

  @Input() _isOverride: boolean;

  addRemoveList: Array<SelectList> = [
    { id: 0, name: "Add Monitoring Hours" },
    { id: 1, name: "Remove Monitoring Hours" },
  ];

  devices: Array<number>;
  calSchedOverrides: CalendarSchedule[] = [];

  // convenience getter for easy access to form fields
  get f() {
    return this.createScheduleOverrideForm.controls;
  }

  protected _onDestroy = new Subject();

  constructor(
    private activeModal: NgbActiveModal,
    private monitoringService: MonitoringService,
    private schedulerService: SchedulerService,
    private readonly toastService: ToastService,
    private readonly _router: Router) { }

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.createScheduleOverrideForm = new FormGroup({
      monitoringHours: new FormControl(1),
      from: new FormControl(this.schedulerService.getUtcDate(this.originalStartDate).toISOString().substring(0, 16)),
      to: new FormControl(this.schedulerService.getUtcDate(this.originalEndDate).toISOString().substring(0, 16)),
      allDay: new FormControl(false),
    });
  }

  dismissModal() {
    this.activeModal.close();
  }

  closeModal() {
    if (this._isOverride === false) {
      //Delete Event
      this.deleteExceptions(this.calSchedOverride);
    }
    else if (this.calEventsToOverride === undefined) {
      // Create Monitored/Non-Monitored Exception
      this.createOverride(this.calSchedOverride);
    }
    else {
      for (let event of this.calEventsToOverride) {
        let calSchedOverride = this.adjustedCalendarSchedules.find(sched => sched.id = Number(event.groupId));
        this.calSchedOverrides.push(calSchedOverride!);
      }

      const unique = [...new Set(this.calSchedOverrides)]

      for (let sched of unique) {
        this.createOverride(sched);
      }

    }
  }

  createOverride(calSchedOverride: CalendarSchedule) {
    let scheduleExceptionsToAdd: Array<ScheduleExceptionDto> = [];

    this.devices = this.schedulerService.getDeviceIDsFromParent(calSchedOverride, this.calendarSchedules);

    if (this.f.allDay.value) {
      this.originalStartDate.setHours(0, 0, 0, 0);
      this.originalEndDate.setHours(23, 59, 0, 0);
    }
    else {
      this.originalStartDate = new Date(this.f.from.value);
      this.originalEndDate = new Date(this.f.to.value);
    }

    this.devices.forEach(deviceID => {
      let overrideException = new ScheduleExceptionDto();
      overrideException.deviceId = deviceID;
      overrideException.startTime = this.schedulerService.getUtcDate(this.originalStartDate);
      overrideException.endTime = this.schedulerService.getUtcDate(this.originalEndDate);
      overrideException.isMonitored = this.f.monitoringHours.value === 0;
      scheduleExceptionsToAdd.push(overrideException);
    });

    if (calSchedOverride.isAssigned === false) {
      let deviceTemplateArray: Array<DeviceTemplate> = [];;

      this.devices.forEach(deviceId => {
        let deviceTemplate = new DeviceTemplate();
        deviceTemplate.deviceId = deviceId;
        deviceTemplateArray.push(deviceTemplate);
      });


      this.monitoringService.assignDeviceScheduleTemplate(calSchedOverride.intervalID, deviceTemplateArray)
        .pipe(takeUntil(this._onDestroy))
        .subscribe({
          next: () => {
            this.toastService.success('Entities Assigned to Template Successfully');
            this.createOrEditExceptions(scheduleExceptionsToAdd);
          }
        })
      this.toastService.info('Assigning Template to Entities...');
    }
    else {
      this.createOrEditExceptions(scheduleExceptionsToAdd);
    }
  }

  //#region Exception Functions
  createOrEditExceptions(scheduleExceptionsToAdd: ScheduleExceptionDto[]) {
    this.monitoringService.createOrEditExceptions(scheduleExceptionsToAdd, false)
      .pipe(takeUntil(this._onDestroy))
      .subscribe({
        next: () => {
          if (this.f.monitoringHours.value == 0) {
            this.toastService.success('Monitored Override Created Successfully');
          }
          else {
            this.toastService.success('Non-Monitored Override Created Successfully');
          }
          this.activeModal.close(true);
        }
      });
    if (this.f.monitoringHours.value == 0) {
      this.toastService.info('Creating Monitored Override...');
    }
    else {
      this.toastService.info('Creating Non-Monitored Override...');
    }

  }

  deleteExceptions(calendarData: CalendarSchedule) {
    //Collect the schedules that needs to be deleted based on the parent
    const schedsToDelete = this.schedulerService.getScheduleExceptionsFromParent(calendarData, this.calendarSchedules);

    let override = new DeleteOverrideDto();
    override.overrideId = calendarData.id;
    override.nvrs = [];

    let nvrMap: any = {};
    schedsToDelete.forEach(input => {
      if (nvrMap[input.nvrID]) {
        if (input.cameraID) {
          nvrMap[input.nvrID].cameras.push(input.cameraID);
        }
      }
      else {
        let nvr: any = {};
        nvr.deviceId = input.nvrID;
        nvr.siteId = input.siteID;
        if (input.cameraID) {
          nvr.cameras = [input.cameraID];
        }
        else {
          nvr.cameras = []
        }
        nvrMap[input.nvrID] = nvr;
      }
    });

    for (let key in nvrMap) {
      override.nvrs?.push(nvrMap[key]);
    }

    const url = '/main/monitoring/schedules/overrides';
    const queryParams = {
      fromDate: this.formatDate(calendarData.startTime),
      toDate: this.formatDate(calendarData.endTime),
      customerNameSearch: this.selectedCustomerName,
      siteNameSearch: this.selectedSiteName,
      nvrNameSearch: this.selectedNvrName,
      overrideType: OverrideTypeLookup['Monitoring Schedule']
    };

    this.activeModal.close(true);
    this._router.navigate([url], { queryParams });
  }
  //#endregion Exception Functions

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); 
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`; 
  }

  ngOnDestroy(): void {
    this._onDestroy.complete();
  }
}
